import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import { useApplicationConfigStore } from '~/store/application-config'
import { useRootStore } from '~/store/root'

// BR-11/BR-12: the "Upload statements" destination is the application flow's own documents step
// while the borrower is still there (any step, not just the bank-statements module itself - the
// chip/popover/toast are visible app-wide), carrying ?upload=manual so MultiAggBankStatementsAppModule
// opens straight on manual upload. On a portal route, or anywhere else that isn't the application
// flow, it's the portal's documents page - not the dashboard's #missing-statements-upload card,
// which is absent for borrowers the failed-pull prompt still reaches (approvals, the default
// dashboard, IL's /portal redirect). Shared by the popover and the failure toast so neither
// re-derives this decision.
export function useStatementUploadDestination() {
  const applicationConfigStore = useApplicationConfigStore()
  const { isEmbedded } = storeToRefs(useRootStore())
  const route = useRoute()

  const disableManualBankStatement = computed(() => (
    applicationConfigStore.currentModule?.disableManualBankStatement ?? false
  ))

  // Exposed (not just used internally by requestUpload) so the popover/toast's <a> tags can bind
  // a real href - a hardcoded href="#" shows the borrower a dead link on hover, even though the
  // click handler always overrides it with a real client-side navigation.
  const destination = computed(() => {
    const inApplicationView = route.path.includes('/application')
    // Portal routes and the "neither" fallback both land on the same destination.
    return inApplicationView
      ? `${isEmbedded.value ? '/embedded' : ''}/application/documents?upload=manual`
      : `${isEmbedded.value ? '/embedded' : ''}/portal/documents`
  })

  async function requestUpload() {
    if (disableManualBankStatement.value) return

    // Already on the destination page (only the query differs, e.g. on the documents step): replace
    // rather than push, since MultiAggBankStatementsAppModule strips ?upload=manual with its own
    // replace - pushing here would leave a duplicate history entry and a first Back that does nothing.
    const replace = route.path === destination.value.split('?')[0]
    try {
      await navigateTo(destination.value, { replace })
    } catch (err) {
      log.error('useStatementUploadDestination failed to navigate to the upload destination', { err, destination: destination.value })
    }
  }

  return {
    destination,
    disableManualBankStatement,
    requestUpload,
  }
}
